import { alpha, Box, CircularProgress, Grid, SxProps, Tooltip, useTheme } from "@mui/material";
import { useGetScenesQuery } from "../../services/scene.service";
import { Params, useParams } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";

import SelectUser from "../../assets/images/undraw_select_hajr.svg";
import { VTButton, VTTypography, VTZoomInOut } from "@virtus-tech-repository/virtus-tech-repository";
import { ITracking, ITrackingActions } from "../../models/tracking.models";
import ScoresItem from "./ScoresItem";
import { useEffect, useState } from "react";
import { setCurrentSidePanel } from "../../store/slices/current.slice";
import { useDispatch } from "react-redux";
import {
    CancelRounded,
    VisibilityRounded,
    VisibilityOffRounded,
    FilterListRounded,
    FilterListOffRounded,
} from "@mui/icons-material";

import NoUser from "../../assets/images/NoScenarios.svg";
import { useGetScenarioOverviewQuery } from "../../services/scenario.service";

interface IProps {
    drawWidth: number;
    sx?: SxProps;
    viewMarks?: boolean;
    correctSceneList?: string[];
}

export default function VTScoresContainer({ drawWidth, sx, viewMarks = false, correctSceneList }: IProps) {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { scenarioId }: Readonly<Params<string>> = useParams();

    const { currentSidePanel, selectedAttempt, selectedUser, currentSidePannelDesiredWidth } = useAppSelector(
        (state) => state.currentReducer,
    );

    const { data: scenarioOverviewData } = useGetScenarioOverviewQuery(scenarioId ? scenarioId : "");
    const [filtered, setFiltered] = useState<boolean>(viewMarks);
    const [filteredAttempt, setFilteredAttempt] = useState<any>(undefined);

    useEffect(() => {
        if (!selectedUser) {
            dispatch(
                setCurrentSidePanel({
                    currentTab: "Users",
                    currentWidth: currentSidePannelDesiredWidth,
                    option: undefined,
                }),
            );
        }
    }, []);

    useEffect(() => {
        if (selectedAttempt && filtered) {
            setFilteredAttempt(
                selectedAttempt.data.actions.filter(
                    (filterElement: any) =>
                        filterElement.sceneId === "c8077f90-da47-4168-91aa-c9233fed82d3" ||
                        filterElement.sceneId === "19965592-557d-4de3-a475-e7673e040015" ||
                        filterElement.sceneId === "7e80b2ac-0943-4480-b94b-5c019b2650f5" ||
                        filterElement.sceneId === "b2f4b70b-6e8e-4a05-8798-d2c272b74eaa" ||
                        filterElement.sceneId === "f096f3fc-5796-41f1-b8e2-ded339d7080c" ||
                        filterElement.sceneId === "bd59080f-8c59-4eae-9139-72c08e7e6afa",
                ),
            );
        } else if (selectedAttempt) {
            setFilteredAttempt(selectedAttempt.data.actions);
        }
    }, [selectedAttempt, filtered]);

    const [scoresTreeZoom, setScoresTreeZoom] = useState<number>(0.6);

    return (
        <Grid
            sx={{
                height: `calc(100vh - 70px - ${drawWidth}px)`,
                zIndex: 100,
                overflowY: "scroll",
                paddingTop: "30px",
                paddingBottom: "50px",
                backgroundColor: theme.palette.background.default,
                position: "relative",
                // zoom: 0.5,
                ...sx,
            }}
            xs={12}
            container
            alignContent={"flex-start"}
            justifyContent={"center"}
        >
            {selectedUser ? (
                <>
                    <VTZoomInOut
                        sx={{
                            marginLeft: "5px",
                            position: "absolute",
                            top: "35px",
                            left: "50px",
                        }}
                        zoomVal={scoresTreeZoom}
                        onZoomIn={() => {
                            if (scoresTreeZoom > 1) return;
                            setScoresTreeZoom(scoresTreeZoom + 0.05);
                        }}
                        onZoomOut={() => {
                            if (scoresTreeZoom < 0.45) return;
                            setScoresTreeZoom(scoresTreeZoom - 0.05);
                        }}
                    />

                    <VTTypography sx={{ marginBottom: "20px" }} type={"Heading2"}>
                        {selectedUser.email.split("@")[0]} actions
                    </VTTypography>
                    {viewMarks && (
                        <Tooltip title={"Only show marked scenes"}>
                            <div style={{ position: "absolute", top: "40px", right: "40px", height: "35px" }}>
                                <VTButton
                                    onClick={() => {
                                        setFiltered(!filtered);
                                    }}
                                    type="icon"
                                    sx={{
                                        background: filtered
                                            ? alpha(theme.palette.primary.light, 0.5)
                                            : alpha(theme.palette.primary.light, 0.2),
                                        height: "35px",
                                        paddingRight: filtered ? "8px" : "40px",
                                        paddingLeft: filtered ? "40px" : "8px",
                                        color: theme.palette.text.primary,
                                        // transition: "0.5s",
                                        "&:hover": {
                                            background: filtered
                                                ? alpha(theme.palette.primary.light, 0.6)
                                                : alpha(theme.palette.primary.light, 0.3),
                                        },
                                    }}
                                >
                                    {filtered ? (
                                        <FilterListRounded
                                            sx={{
                                                background: theme.palette.primary.main,
                                                borderRadius: "50%",
                                                padding: "4px",
                                                color: "#fff",
                                                "&:hover": {
                                                    background: theme.palette.primary.dark,
                                                },
                                            }}
                                        />
                                    ) : (
                                        <FilterListOffRounded
                                            sx={{
                                                background: theme.palette.primary.main,
                                                borderRadius: "50%",
                                                padding: "4px",
                                                color: "#fff",
                                                "&:hover": {
                                                    background: theme.palette.primary.dark,
                                                },
                                            }}
                                        />
                                    )}
                                </VTButton>
                            </div>
                        </Tooltip>
                    )}
                    {scenarioOverviewData &&
                        filteredAttempt &&
                        filteredAttempt.map((sceneActionElement: ITrackingActions, sceneActionElementIndex: number) => {
                            return (
                                <ScoresItem
                                    zoomValue={scoresTreeZoom}
                                    sceneActionElement={sceneActionElement}
                                    scenes={scenarioOverviewData.scenes}
                                    furtherScenes={sceneActionElementIndex !== filteredAttempt.length - 1}
                                    showScenes={!viewMarks}
                                    marked={
                                        viewMarks
                                            ? correctSceneList?.includes(sceneActionElement.sceneId)
                                                ? true
                                                : sceneActionElement.sceneId ===
                                                      "c8077f90-da47-4168-91aa-c9233fed82d3" ||
                                                  sceneActionElement.sceneId ===
                                                      "19965592-557d-4de3-a475-e7673e040015" ||
                                                  sceneActionElement.sceneId ===
                                                      "7e80b2ac-0943-4480-b94b-5c019b2650f5" ||
                                                  sceneActionElement.sceneId ===
                                                      "b2f4b70b-6e8e-4a05-8798-d2c272b74eaa" ||
                                                  sceneActionElement.sceneId ===
                                                      "f096f3fc-5796-41f1-b8e2-ded339d7080c" ||
                                                  sceneActionElement.sceneId === "bd59080f-8c59-4eae-9139-72c08e7e6afa"
                                                ? false
                                                : undefined
                                            : undefined
                                    }
                                />
                            );
                        })}
                </>
            ) : (
                <Grid container sx={{ height: "100%" }} justifyContent={"center"} alignContent={"center"}>
                    <Grid item xs={12} container justifyContent={"center"}>
                        <VTTypography type={"Heading2"} sx={{ marginBottom: "30px" }}>
                            No user selected
                        </VTTypography>
                    </Grid>
                    <Grid item>
                        <img src={NoUser} />
                    </Grid>{" "}
                    <Grid item xs={12} container justifyContent={"center"}>
                        <VTTypography type={"Heading4"} sx={{ marginTop: "20px" }}>
                            Please select a user from the side panel on the left
                        </VTTypography>
                    </Grid>
                    <Grid item xs={12} container justifyContent={"center"}>
                        <VTButton
                            onClick={() => {
                                dispatch(
                                    setCurrentSidePanel({
                                        currentTab: "Users",
                                        currentWidth: currentSidePannelDesiredWidth,
                                        option: undefined,
                                    }),
                                );
                            }}
                        >
                            Choose user
                        </VTButton>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}
